import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import type { HospitalRegion } from "components/pages/settings/context/types";

import { getExhClient, isExhClientInitialized } from "./client";

export interface HospitalGroup {
  id: string;
  region: HospitalRegion;
  name: string | null;
}

const DEFAULT_REGION: HospitalRegion = "USA";

const isHospitalRegion = (value: unknown): value is HospitalRegion => value === "USA" || value === "UK";

/**
 * Shared helper to find a group document by ID
 */
const findGroupDocument = async (groupId: string) => {
  if (!isExhClientInitialized()) {
    throw new Error("ExtraHorizon client not initialized");
  }
  const client = getExhClient();
  const rql = rqlBuilder().eq("id", groupId).limit(1).build();
  const response = await client.data.documents.find("group", { rql });
  return response?.data?.[0] as { id?: string; data?: { config?: unknown; name?: string } } | undefined;
};

/**
 * Read a group document from the ExH data service (`group` schema) and
 * return its region and name. The legacy `client.groups` SDK service is
 * deprecated — groups are stored as documents in the data service.
 * Defaults to "USA" region when the document or `data.config` is missing.
 * Returns null for name when not found.
 *
 * 401/403 are surfaced as a default fallback so the settings page
 * still renders for users without group read permission.
 */
export const getHospitalGroup = async (hospitalId: string): Promise<HospitalGroup> => {
  try {
    const doc = await findGroupDocument(hospitalId);
    const region = isHospitalRegion(doc?.data?.config) ? doc.data.config : DEFAULT_REGION;
    const name = doc?.data?.name || null;
    return { id: hospitalId, region, name };
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    if (status === 401 || status === 403) {
      return { id: hospitalId, region: DEFAULT_REGION, name: null };
    }
    throw err;
  }
};

/**
 * Get hospital name from group document
 * @param groupId - ID of the group/hospital
 * @returns Hospital name from group document or null if not found
 */
export const getHospitalName = async (groupId: string): Promise<string | null> => {
  const g = await getHospitalGroup(groupId);
  return g.name;
};
